import React from 'react';
import '../components/BoardColumnFilter.css';
import { BoardSearchCidadeFilterPanel } from './BoardSearchCidadeFilterPanel';
import { BoardSearchSimplePickerPanel } from './BoardSearchSimplePickerPanel';
import { sortByCountDesc } from './filterChipLayout';
import { SIMPLE_PICKER_BULK_MENU_TYPES } from './simplePickerFilterTypes';
import { TIPO_OS_NAMES } from './tipoOsFilterUtils';

const SLA_NAMES = {
  vencido: 'Vencido',
  vencendo: 'Vencendo',
  ok: 'A vencer',
};

export { CompactFooter } from './BoardSearchCompactFooter';

function renderSimplePicker({
  filterType,
  items,
  getKey,
  getLabel,
  getSuffix,
  selectedKeys,
  isSelected,
  onToggle,
  searchPlaceholder = 'Buscar',
  searchTerm,
  onSearchChange,
  showSearch = true,
  emptyMessage,
  listAriaLabel,
  tempFilters,
  setTempFilters,
  onApplyLive,
  selectionField,
}) {
  const bulkMenuEnabled = SIMPLE_PICKER_BULK_MENU_TYPES.has(filterType);
  const keys = items.map(getKey);
  const selected = selectedKeys ?? (selectionField ? tempFilters[selectionField] : []);
  const allMarked =
    keys.length > 0 &&
    (isSelected
      ? keys.every((k) => items.find((i) => getKey(i) === k) && isSelected(items.find((i) => getKey(i) === k)))
      : keys.every((k) => selected.includes(k)));

  const handleBulkToggle = () => {
    if (filterType === 'retorno') {
      const next = tempFilters.retorno
        ? { ...tempFilters, retorno: null }
        : { ...tempFilters, retorno: 'sim' };
      setTempFilters(next);
      onApplyLive?.(next);
      return;
    }
    if (!selectionField) return;
    const newSelected = allMarked
      ? selected.filter((s) => !keys.includes(s))
      : Array.from(new Set([...selected, ...keys]));
    const next = { ...tempFilters, [selectionField]: newSelected };
    setTempFilters(next);
    onApplyLive?.(next);
  };

  return (
    <BoardSearchSimplePickerPanel
      searchPlaceholder={searchPlaceholder}
      searchTerm={searchTerm}
      onSearchChange={onSearchChange}
      showSearch={showSearch}
      items={items}
      getKey={getKey}
      getLabel={getLabel}
      getSuffix={getSuffix}
      isSelected={isSelected}
      selectedKeys={selectedKeys}
      onToggle={onToggle}
      emptyMessage={emptyMessage}
      listAriaLabel={listAriaLabel}
      bulkMenuEnabled={bulkMenuEnabled}
      bulkExclusive={filterType === 'retorno'}
      onBulkToggleAll={bulkMenuEnabled ? handleBulkToggle : undefined}
    />
  );
}

export function BoardSearchFilterPopoverContent({
  filterType,
  tempFilters,
  searchTerm,
  setSearchTerm,
  applyFilters,
  clearFilters,
  filteredBairros,
  filteredClientes,
  filteredEquipamentos,
  filteredStatus,
  filteredTecnicoInicial,
  filteredDefeitoDescricao,
  filteredDefeitoAlert,
  filteredDefeitoClassification,
  filteredDefeitoSeverity,
  citiesByEstado,
  tiposOSWithCounts,
  slasWithCounts,
  toggleBairroFilter,
  toggleClienteFilter,
  toggleEquipamentoFilter,
  toggleStatusFilter,
  toggleTecnicoInicialFilter,
  toggleTipoOSFilter,
  toggleSLAFilter,
  toggleCityFilter,
  toggleEstadoFilter,
  onSetCidades,
  toggleDefeitoDescricaoFilter,
  toggleDefeitoAlertFilter,
  toggleDefeitoClassificationFilter,
  toggleDefeitoSeverityFilter,
  setTempFilters,
  onApplyLive,
  citiesWithCounts = [],
}) {
  if (filterType === 'cidade') {
    return (
      <BoardSearchCidadeFilterPanel
        citiesWithCounts={citiesWithCounts}
        citiesByEstado={citiesByEstado}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedCidades={tempFilters.cidade}
        onToggleCity={toggleCityFilter}
        onToggleEstado={toggleEstadoFilter}
        onSetCidades={onSetCidades}
      />
    );
  }

  if (filterType === 'retorno') {
    const items = [
      { key: 'sim', label: 'Sim' },
      { key: 'nao', label: 'Não' },
    ];
    return renderSimplePicker({
      filterType: 'retorno',
      items,
      getKey: (i) => i.key,
      getLabel: (i) => i.label,
      isSelected: (i) => tempFilters.retorno === i.key,
      onToggle: (i) => {
        const next = { ...tempFilters, retorno: i.key };
        setTempFilters(next);
        onApplyLive?.(next);
      },
      showSearch: false,
      emptyMessage: 'Nenhuma opção',
      listAriaLabel: 'Retorno',
      tempFilters,
      setTempFilters,
      onApplyLive,
    });
  }

  const listConfigs = {
    bairro: {
      items: filteredBairros,
      getKey: (b) => (typeof b === 'string' ? b : b.bairro),
      getLabel: (b) => (typeof b === 'string' ? b : b.bairro),
      getSuffix: (b) => (typeof b === 'object' ? b.count : undefined),
      selected: tempFilters.bairro,
      onToggle: (item) => toggleBairroFilter(typeof item === 'string' ? item : item.bairro),
      field: 'bairro',
      empty: 'Nenhum bairro encontrado',
      aria: 'Bairros',
    },
    cliente: {
      items: filteredClientes,
      getKey: (c) => (typeof c === 'string' ? c : c.cliente),
      getLabel: (c) => (typeof c === 'string' ? c : c.cliente),
      getSuffix: (c) => (typeof c === 'object' ? c.count : undefined),
      selected: tempFilters.cliente,
      onToggle: (item) => toggleClienteFilter(typeof item === 'string' ? item : item.cliente),
      field: 'cliente',
      empty: 'Nenhum cliente encontrado',
      aria: 'Clientes',
    },
    equipamento: {
      items: filteredEquipamentos,
      getKey: (i) => i.equipamento,
      getLabel: (i) => i.equipamento,
      getSuffix: (i) => i.count,
      selected: tempFilters.equipamento,
      onToggle: (item) => toggleEquipamentoFilter(item.equipamento),
      field: 'equipamento',
      empty: searchTerm ? `Nenhum equipamento para "${searchTerm}"` : 'Nenhum equipamento encontrado',
      aria: 'Equipamentos',
    },
    status: {
      items: filteredStatus,
      getKey: (i) => i.status,
      getLabel: (i) => i.status,
      getSuffix: (i) => i.count,
      selected: tempFilters.status,
      onToggle: (item) => toggleStatusFilter(item.status),
      field: 'status',
      empty: searchTerm ? `Nenhum status para "${searchTerm}"` : 'Nenhum status encontrado',
      aria: 'Status',
    },
    tecnicoInicial: {
      items: filteredTecnicoInicial,
      getKey: (i) => i.tecnicoInicial,
      getLabel: (i) => i.tecnicoInicial,
      getSuffix: (i) => i.count,
      selected: tempFilters.tecnicoInicial || [],
      onToggle: (item) => toggleTecnicoInicialFilter(item.tecnicoInicial),
      field: 'tecnicoInicial',
      empty: searchTerm ? `Nenhum técnico para "${searchTerm}"` : 'Nenhum técnico inicial encontrado',
      aria: 'Técnicos iniciais',
    },
    defeitoDescricao: {
      items: filteredDefeitoDescricao,
      getKey: (i) => i.value,
      getLabel: (i) => i.value,
      getSuffix: (i) => i.count,
      selected: tempFilters.defeitoDescricao,
      onToggle: (item) => toggleDefeitoDescricaoFilter(item.value),
      field: 'defeitoDescricao',
      empty: 'Nenhum resultado',
      aria: 'Descrições de defeito',
    },
    defeitoAlert: {
      items: filteredDefeitoAlert,
      getKey: (i) => i.value,
      getLabel: (i) => i.value,
      getSuffix: (i) => i.count,
      selected: tempFilters.defeitoAlert,
      onToggle: (item) => toggleDefeitoAlertFilter(item.value),
      field: 'defeitoAlert',
      empty: 'Nenhum resultado',
      aria: 'Códigos de alerta',
    },
    defeitoClassification: {
      items: filteredDefeitoClassification,
      getKey: (i) => i.value,
      getLabel: (i) => i.value,
      getSuffix: (i) => i.count,
      selected: tempFilters.defeitoClassification,
      onToggle: (item) => toggleDefeitoClassificationFilter(item.value),
      field: 'defeitoClassification',
      empty: 'Nenhum resultado',
      aria: 'Classificações',
    },
    defeitoSeverity: {
      items: filteredDefeitoSeverity,
      getKey: (i) => i.value,
      getLabel: (i) => i.value,
      getSuffix: (i) => i.count,
      selected: tempFilters.defeitoSeverity,
      onToggle: (item) => toggleDefeitoSeverityFilter(item.value),
      field: 'defeitoSeverity',
      empty: 'Nenhum resultado',
      aria: 'Criticidades',
    },
  };

  if (listConfigs[filterType]) {
    const cfg = listConfigs[filterType];
    return renderSimplePicker({
      filterType,
      items: cfg.items,
      getKey: cfg.getKey,
      getLabel: cfg.getLabel,
      getSuffix: cfg.getSuffix,
      selectedKeys: cfg.selected,
      onToggle: cfg.onToggle,
      searchTerm,
      onSearchChange: setSearchTerm,
      emptyMessage: cfg.empty,
      listAriaLabel: cfg.aria,
      tempFilters,
      setTempFilters,
      onApplyLive,
      selectionField: cfg.field,
    });
  }

  if (filterType === 'tipoOS') {
    const sortedTipos = sortByCountDesc(tiposOSWithCounts);
    const filteredTipos = sortedTipos.filter((item) => {
      if (!searchTerm?.trim()) return true;
      const label = (TIPO_OS_NAMES[item.tipo] || item.tipo).toLowerCase();
      return label.includes(searchTerm.toLowerCase().trim());
    });

    return renderSimplePicker({
      filterType: 'tipoOS',
      items: filteredTipos,
      getKey: (i) => i.tipo,
      getLabel: (i) => TIPO_OS_NAMES[i.tipo] || i.tipo,
      getSuffix: (i) => i.count,
      selectedKeys: tempFilters.tipoOS,
      onToggle: (item) => toggleTipoOSFilter(item.tipo),
      searchTerm,
      onSearchChange: setSearchTerm,
      emptyMessage: 'Nenhum tipo encontrado',
      listAriaLabel: 'Tipos de OS',
      tempFilters,
      setTempFilters,
      onApplyLive,
      selectionField: 'tipoOS',
    });
  }

  if (filterType === 'sla') {
    const sortedSlas = sortByCountDesc(slasWithCounts);
    const filteredSlas = sortedSlas.filter((item) => {
      if (!searchTerm?.trim()) return true;
      const label = (SLA_NAMES[item.sla] || item.sla).toLowerCase();
      return label.includes(searchTerm.toLowerCase().trim());
    });

    return renderSimplePicker({
      filterType: 'sla',
      items: filteredSlas,
      getKey: (i) => i.sla,
      getLabel: (i) => SLA_NAMES[i.sla] || i.sla,
      getSuffix: (i) => i.count,
      selectedKeys: tempFilters.sla,
      onToggle: (item) => toggleSLAFilter(item.sla),
      searchTerm,
      onSearchChange: setSearchTerm,
      emptyMessage: 'Nenhum SLA encontrado',
      listAriaLabel: 'SLA',
      tempFilters,
      setTempFilters,
      onApplyLive,
      selectionField: 'sla',
    });
  }

  return null;
}
